import React, { useCallback, useEffect, useMemo, useRef } from "react";

import {
  faClone,
  faCopy,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import {
  getSchemaTypeIcon,
  isBaseSchemaType,
} from "src/base-components/SchemaTypeSelector";
import {
  CellDisplayType,
  getAllKeys,
  getClearedValue,
  isExplicitActivationCell,
  resolveColumnType,
} from "src/cases/caseView/blocks/cellTypes";
import { EditableTableCell } from "src/cases/caseView/blocks/EditableTableCell";
import {
  createEditableTableSelectionStore,
  EditableTableSelectionProvider,
} from "src/cases/caseView/blocks/useEditableTableSelection";
import { UseTableBlockEditsReturn } from "src/cases/caseView/blocks/useTableBlockEdits";
import { useIsBlockExpanded } from "src/cases/caseView/CaseBlock";
import { TableBlockMultiColumnRecord } from "src/cases/queries/casesTypes";
import {
  TableColumnConfigOutput,
  TableEditMode,
} from "src/clients/case-management";
import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { copyTextToClipboard } from "src/utils/clipboard";

type ColumnsConfig = Record<string, TableColumnConfigOutput> | null | undefined;

const COLUMN_STYLE = { minWidth: 160, maxWidth: 350 } as const;

type EditableTableBlockProps = {
  records: TableBlockMultiColumnRecord;
  columnsConfig: ColumnsConfig;
  defaultColumnsEditMode?: TableEditMode;
  edits: UseTableBlockEditsReturn;
};

const helper = createColumnHelper<Record<string, unknown>>();

const isColumnEditable = (
  columnKey: string,
  columnsConfig: ColumnsConfig,
  defaultColumnsEditMode?: TableEditMode,
): boolean => {
  if (!columnsConfig) return true;
  const config = columnsConfig[columnKey];
  if (!config) return defaultColumnsEditMode !== TableEditMode.READ_ONLY;
  return config.edit_mode !== TableEditMode.READ_ONLY;
};

const getColumnDisplayName = (
  columnKey: string,
  columnsConfig: ColumnsConfig,
): string => {
  const config = columnsConfig?.[columnKey];
  return config?.display_name || columnKey;
};

const getColumnDisplayType = (
  columnKey: string,
  columnsConfig: ColumnsConfig,
): string | null => {
  const config = columnsConfig?.[columnKey];
  return config?.display_config?.type ?? null;
};

export const EditableTableBlock: React.FC<EditableTableBlockProps> = ({
  records,
  columnsConfig,
  defaultColumnsEditMode,
  edits,
}) => {
  const isExpanded = useIsBlockExpanded();
  const {
    editedData,
    modifiedCells,
    updateCell,
    addRow,
    deleteRow,
    duplicateRow,
  } = edits;

  const storeRef = useRef(createEditableTableSelectionStore());
  const store = storeRef.current;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const modifiedCellsRef = useRef<Set<string>>(modifiedCells);
  modifiedCellsRef.current = modifiedCells;

  const editedDataRef = useRef(editedData);
  editedDataRef.current = editedData;

  const columnKeys = useMemo(() => getAllKeys(records), [records]);

  const editableColumnKeys = useMemo(
    () =>
      columnKeys.filter((key) =>
        isColumnEditable(key, columnsConfig, defaultColumnsEditMode),
      ),
    [columnKeys, columnsConfig, defaultColumnsEditMode],
  );

  const columnDisplayTypes = useMemo(
    () =>
      Object.fromEntries(
        columnKeys.map((key) => {
          const displayConfig = columnsConfig?.[key]?.display_config ?? null;
          const representativeValue = records.find(
            (row) => row[key] !== null && row[key] !== undefined,
          )?.[key];
          return [key, resolveColumnType(displayConfig, representativeValue)];
        }),
      ) as Record<string, CellDisplayType>,
    [columnKeys, columnsConfig, records],
  );

  const columnDisplayTypesRef = useRef(columnDisplayTypes);
  columnDisplayTypesRef.current = columnDisplayTypes;

  const columnValuesMap = useMemo(
    () =>
      Object.fromEntries(
        columnKeys.map((key) => [key, editedData.map((row) => row[key])]),
      ) as Record<string, unknown[]>,
    [columnKeys, editedData],
  );

  const columnValuesMapRef = useRef(columnValuesMap);
  columnValuesMapRef.current = columnValuesMap;

  const columnKeysRef = useRef(columnKeys);
  columnKeysRef.current = columnKeys;

  const editableColumnKeysRef = useRef(editableColumnKeys);
  editableColumnKeysRef.current = editableColumnKeys;

  // DOM side effects: focus container and scroll cell into view on selection change.
  // Reads the LATEST store state inside the rAF so stale callbacks from rapid
  // transitions never steal focus from an editor that opened in the meantime.
  useEffect(() => {
    const unsub = store.subscribe((state, prev) => {
      if (state.selectedCell === prev.selectedCell) return;

      if (state.selectedCell) {
        requestAnimationFrame(() => {
          const current = store.getState().selectedCell;
          if (!current) return;

          // When editing is active, a portal-rendered editor (boolean
          // dropdown, date popover, JSON FloatingWindow) owns focus.
          // Don't steal it — the editor is outside the container in the DOM
          // so the contains() check below would incorrectly refocus.
          if (!current.editing) {
            if (
              tableContainerRef.current &&
              !tableContainerRef.current.contains(document.activeElement)
            ) {
              tableContainerRef.current.focus();
            }
          }

          // Scroll cell into view
          const { row, col } = current;
          const el = tableContainerRef.current?.querySelector(
            `[data-loc="editable-table-cell-${row}-${col}"]`,
          );
          el?.scrollIntoView({ block: "nearest", inline: "nearest" });
        });
      }
    });
    return unsub;
  }, [store]);

  /** Blur the active editor inside the table to commit its pending value. */
  const blurActiveEditor = useCallback(() => {
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== tableContainerRef.current &&
      tableContainerRef.current?.contains(document.activeElement)
    ) {
      document.activeElement.blur();
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const sel = store.getState().selectedCell;
      if (!sel) return;

      const { row, col } = sel;
      const keys = columnKeysRef.current;
      const editableKeys = editableColumnKeysRef.current;
      const dataLength = editedDataRef.current.length;
      const { selectCell, deselectCell, setEditing } = store.getState().actions;

      // Tab is handled before the portal guard so it works from
      // portaled editors (boolean dropdown, date picker, etc.).
      if (e.key === "Tab") {
        e.preventDefault();
        if (editableKeys.length === 0) return;

        const currentEditableIndex = editableKeys.indexOf(col);

        if (e.shiftKey) {
          if (currentEditableIndex > 0) {
            blurActiveEditor();
            selectCell(row, editableKeys[currentEditableIndex - 1]);
          } else if (row > 0) {
            blurActiveEditor();
            selectCell(row - 1, editableKeys[editableKeys.length - 1]);
          }
        } else {
          if (currentEditableIndex < editableKeys.length - 1) {
            blurActiveEditor();
            selectCell(row, editableKeys[currentEditableIndex + 1]);
          } else if (row < dataLength - 1) {
            blurActiveEditor();
            selectCell(row + 1, editableKeys[0]);
          }
        }
        return;
      }

      // Ignore portal-rendered editor events (React synthetic events
      // bubble through portals even though the DOM target is outside).
      if (
        e.target instanceof Node &&
        !tableContainerRef.current?.contains(e.target)
      ) {
        return;
      }

      const displayTypes = columnDisplayTypesRef.current;
      const colIndex = keys.indexOf(col);
      const cellType = displayTypes[col]?.type;

      // Let explicit-activation editors own all remaining keys.
      if (sel.editing && cellType && isExplicitActivationCell(cellType)) return;

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          deselectCell();
          break;

        case "Enter": {
          if (
            cellType === "string" ||
            cellType === "number" ||
            cellType === "currency"
          ) {
            e.preventDefault();
            if (row < dataLength - 1) {
              selectCell(row + 1, col);
            } else {
              deselectCell();
            }
          } else if (cellType && isExplicitActivationCell(cellType)) {
            e.preventDefault();
            setEditing();
          }
          break;
        }

        case "Backspace":
        case "Delete": {
          const clearedValue = getClearedValue(displayTypes[col]?.type);
          if (clearedValue !== undefined) {
            e.preventDefault();
            updateCell(row, col, clearedValue);
          }
          break;
        }

        case "ArrowUp":
          e.preventDefault();
          if (row > 0) {
            blurActiveEditor();
            selectCell(row - 1, col);
          }
          break;

        case "ArrowDown":
          e.preventDefault();
          if (row < dataLength - 1) {
            blurActiveEditor();
            selectCell(row + 1, col);
          }
          break;

        case "ArrowLeft":
          e.preventDefault();
          if (colIndex > 0) {
            blurActiveEditor();
            selectCell(row, keys[colIndex - 1]);
          }
          break;

        case "ArrowRight":
          e.preventDefault();
          if (colIndex < keys.length - 1) {
            blurActiveEditor();
            selectCell(row, keys[colIndex + 1]);
          }
          break;
      }
    },
    [store, updateCell, blurActiveEditor],
  );

  const columns = useMemo(() => {
    return columnKeys.map((key) =>
      helper.accessor((row) => row[key], {
        id: key,
        header: () => {
          const displayName = getColumnDisplayName(key, columnsConfig);
          const displayType = getColumnDisplayType(key, columnsConfig);
          const typeIcon =
            displayType && isBaseSchemaType(displayType)
              ? getSchemaTypeIcon(displayType)
              : null;

          return (
            <div className="flex h-8 items-center gap-1.5 pr-2 pl-1.5 font-inter-medium-12px text-gray-800">
              {typeIcon && <Icon icon={typeIcon} size="2xs" />}
              <span className="truncate">{displayName}</span>
            </div>
          );
        },
        cell: (info) => (
          <EditableTableCell
            columnKey={key}
            columnValues={columnValuesMapRef.current[key]}
            displayType={columnDisplayTypes[key] ?? { type: "string" }}
            isEditable={isColumnEditable(
              key,
              columnsConfig,
              defaultColumnsEditMode,
            )}
            isEdited={modifiedCellsRef.current.has(`${info.row.index}:${key}`)}
            rowIndex={info.row.index}
            value={info.getValue()}
            onChange={(newValue) => updateCell(info.row.index, key, newValue)}
          />
        ),
      }),
    );
  }, [
    columnKeys,
    columnsConfig,
    defaultColumnsEditMode,
    updateCell,
    columnDisplayTypes,
  ]);

  const table = useReactTable({
    data: editedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_, index) => String(index),
  });

  const handleDuplicateRow = useCallback(
    (rowIndex: number) => {
      duplicateRow(rowIndex);
      store.getState().actions.adjustForRowInsert(rowIndex);
    },
    [duplicateRow, store],
  );

  const handleCopyRow = useCallback(
    (rowIndex: number) =>
      copyTextToClipboard(
        JSON.stringify(editedDataRef.current[rowIndex], null, 2),
      ),
    [],
  );

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      deleteRow(rowIndex);
      store.getState().actions.adjustForRowDelete(rowIndex);
    },
    [deleteRow, store],
  );

  const rowActionElements = useCallback(
    (rowIndex: number): OptionsDropdownElement[] => [
      {
        key: "Duplicate",
        action: () => handleDuplicateRow(rowIndex),
        icon: faClone,
      },
      {
        key: "Copy payload as JSON",
        action: () => handleCopyRow(rowIndex),
        icon: faCopy,
      },
      {
        key: "Delete",
        action: () => handleDeleteRow(rowIndex),
        icon: faTrashCan,
      },
    ],
    [handleDuplicateRow, handleCopyRow, handleDeleteRow],
  );

  const addRowButton = (
    <Button
      dataLoc="editable-table-add-row"
      iconLeft={faPlus}
      size="sm"
      variant="secondary"
      onClick={addRow}
    >
      Add row
    </Button>
  );

  return (
    <EditableTableSelectionProvider value={store}>
      <div className="flex h-full flex-col">
        <div
          ref={tableContainerRef}
          className={
            isExpanded
              ? "decideScrollbar min-h-0 flex-1 overflow-auto pt-4 pr-5 pb-8 pl-5 outline-none"
              : "decideScrollbar min-h-0 flex-1 overflow-auto p-4 outline-none"
          }
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <table className="w-full border-collapse border-spacing-0">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className="border-gray-100 border-b bg-gray-50"
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="relative p-0 text-left after:absolute after:top-0 after:right-0 after:bottom-0 after:w-px after:bg-gray-100 first:before:absolute first:before:top-0 first:before:bottom-0 first:before:left-0 first:before:w-px first:before:bg-gray-100"
                      style={COLUMN_STYLE}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="group/row border-gray-100 border-b">
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <td
                      key={cell.id}
                      className="relative p-0 after:absolute after:top-0 after:right-0 after:bottom-0 after:w-px after:bg-gray-100 first:before:absolute first:before:top-0 first:before:bottom-0 first:before:left-0 first:before:w-px first:before:bg-gray-100"
                      style={COLUMN_STYLE}
                    >
                      {cellIndex === 0 && (
                        <div className="absolute top-1.5 -left-2 z-10 flex h-5 w-[15px] items-center justify-center rounded border border-gray-200 bg-white opacity-0 group-hover/row:opacity-100">
                          <EllipsisOptionsDropdown
                            dataLoc={`editable-table-row-actions-${row.index}`}
                            elements={rowActionElements(row.index)}
                            iconSize="2xs"
                          />
                        </div>
                      )}
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!isExpanded && <div className="pt-3">{addRowButton}</div>}
        </div>
        {isExpanded && (
          <div className="border-gray-200 border-t p-4">{addRowButton}</div>
        )}
      </div>
    </EditableTableSelectionProvider>
  );
};
